"use client";

import Link from "next/link";
import Image from "next/image";
import { motion } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { getServiceBySlug } from "@/lib/services-data";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";

interface ServiceCardProps {
  slug: string;
  headingLevel?: "h2" | "h3";
}

export function ServiceCard({ slug, headingLevel = "h3" }: ServiceCardProps) {
  const shouldReduceMotion = usePrefersReducedMotion();
  const service = getServiceBySlug(slug);
  if (!service) return null;

  const startingPrice = service.options[0]?.price;
  const Heading = headingLevel;

  return (
    <Link href={`/services/${service.slug}`}>
      <motion.div
        whileHover={
          shouldReduceMotion
            ? undefined
            : { y: -4, boxShadow: "0 12px 24px rgba(0, 0, 0, 0.1)" }
        }
        transition={{ duration: shouldReduceMotion ? 0 : 0.2, ease: "easeOut" }}
      >
        <Card className="h-full cursor-pointer overflow-hidden pt-0">
          <div className="relative aspect-video">
            <Image
              src={service.image}
              alt={service.shortTitle}
              fill
              loading="eager"
              className="object-cover"
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            />
          </div>
          <CardContent className="pt-2">
            <Heading className="text-xl text-warm-gray-dark">
              {service.shortTitle}
            </Heading>
            <p className="text-sm text-warm-gray-light line-clamp-3 mt-2">
              {service.description}
            </p>
            {startingPrice && (
              <p className="text-terracotta font-medium mt-3">
                Starting at {startingPrice}
              </p>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </Link>
  );
}
